import React, { useEffect, useState } from 'react';
import axios from 'axios';
import WorldMapHeatmap from '../components/WorldMapHeatmap';
import TopRegionsTable from '../components/TopRegionsTable';
import TimezoneChart from '../components/TimezoneChart';
import RevenueByRegionChart from '../components/RevenueByRegionChart';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface GeographicDistribution {
  country: string;
  countryName: string;
  patronCount: number;
  revenue: number;
  percentage: number;
  latitude: number | null;
  longitude: number | null;
}

interface TimezoneDistribution {
  timezone: string;
  patronCount: number;
  percentage: number;
  utcOffset: string;
}

interface GeographicSummary {
  totalCountries: number;
  totalRegions: number;
  totalCities: number;
  topCountry: string | null;
  topCountryPatrons: number;
  topTimezone: string | null;
}

/**
 * Geographic Analytics Dashboard
 * Shows patron distribution, revenue by region, timezone analysis
 */
export const GeographicAnalyticsDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Data states
  const [patronDistribution, setPatronDistribution] = useState<GeographicDistribution[]>([]);
  const [revenueDistribution, setRevenueDistribution] = useState<GeographicDistribution[]>([]);
  const [timezoneData, setTimezoneData] = useState<TimezoneDistribution[]>([]);
  const [summary, setSummary] = useState<GeographicSummary | null>(null);
  
  // UI states
  const [selectedMetric, setSelectedMetric] = useState<'patronCount' | 'revenue'>('patronCount');
  const [dateRange, setDateRange] = useState<'all' | '30d' | '90d' | '1y'>('all');

  useEffect(() => {
    fetchGeographicData();
  }, [dateRange]);

  const fetchGeographicData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const headers = {
        Authorization: `Bearer ${token}`,
      };

      // Calculate date filters
      const endDate = new Date();
      let startDate: Date | undefined;

      if (dateRange === '30d') {
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
      } else if (dateRange === '90d') {
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 90);
      } else if (dateRange === '1y') {
        startDate = new Date();
        startDate.setFullYear(startDate.getFullYear() - 1);
      }

      const params = startDate
        ? {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          }
        : {};

      // Fetch all data in parallel
      const [
        distributionRes,
        revenueRes,
        timezoneRes,
        summaryRes,
      ] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/geographic/distribution`, {
          headers,
          params,
        }),
        axios.get(`${API_URL}/api/analytics/geographic/revenue`, {
          headers,
          params,
        }),
        axios.get(`${API_URL}/api/analytics/geographic/timezones`, { headers }),
        axios.get(`${API_URL}/api/analytics/geographic/summary`, { headers }),
      ]);

      setPatronDistribution(distributionRes.data.data || []);
      setRevenueDistribution(revenueRes.data.data || []);
      setTimezoneData(timezoneRes.data.data || []);
      setSummary(summaryRes.data.data || null);
    } catch (err: any) {
      console.error('Error fetching geographic data:', err);
      setError(
        err.response?.data?.message ||
          err.message ||
          'Failed to load geographic analytics'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading geographic analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <div className="flex items-center gap-3 mb-3">
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-lg font-semibold text-red-900">Error</h3>
          </div>
          <p className="text-red-700">{error}</p>
          <button
            onClick={fetchGeographicData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Geographic Analytics
          </h1>
          <p className="text-gray-600">
            Analyze your patron distribution across countries, regions, and timezones
          </p>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Countries</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {summary.totalCountries}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <svg
                    className="w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Regions</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {summary.totalRegions}
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <svg
                    className="w-6 h-6 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Cities</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {summary.totalCities}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Top Country</p>
                  <p className="text-lg font-bold text-gray-900 mt-1 truncate">
                    {summary.topCountry || 'N/A'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {summary.topCountryPatrons} patrons
                  </p>
                </div>
                <div className="p-3 bg-amber-100 rounded-full">
                  <svg
                    className="w-6 h-6 text-amber-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4 mb-8">
          <div className="flex flex-wrap items-center gap-4">
            {/* Date Range Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">
                Date Range:
              </label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Time</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="1y">Last Year</option>
              </select>
            </div>

            {/* Metric Toggle */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Map View:</label>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setSelectedMetric('patronCount')}
                  className={`px-4 py-1 rounded-md text-sm font-medium transition-colors ${
                    selectedMetric === 'patronCount'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Patrons
                </button>
                <button
                  onClick={() => setSelectedMetric('revenue')}
                  className={`px-4 py-1 rounded-md text-sm font-medium transition-colors ${
                    selectedMetric === 'revenue'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Revenue
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* World Map */}
        <div className="mb-8">
          <WorldMapHeatmap
            data={selectedMetric === 'patronCount' ? patronDistribution : revenueDistribution}
            metric={selectedMetric}
          />
        </div>

        {/* Revenue and Timezone Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <RevenueByRegionChart data={revenueDistribution} limit={10} />
          <TimezoneChart data={timezoneData} limit={10} />
        </div>

        {/* Top Regions Table */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Top Countries by {selectedMetric === 'patronCount' ? 'Patron Count' : 'Revenue'}
          </h2>
          <TopRegionsTable
            data={selectedMetric === 'patronCount' ? patronDistribution : revenueDistribution}
            metric={selectedMetric}
            limit={20}
          />
        </div>
      </div>
    </div>
  );
};

export default GeographicAnalyticsDashboard;
